import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Empty,
  Modal,
  Select,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import API_CONFIG from "../../config/api";

const { Text } = Typography;

type AvailabilityGroup =
  | "in_stock"
  | "preorder_or_unlimited"
  | "out_of_stock";

type StoreDisplayOrder = {
  id: number;
  store_name: string;
  store_slug: string;
  product_stock_order: AvailabilityGroup[];
};

const DEFAULT_ORDER: AvailabilityGroup[] = [
  "in_stock",
  "preorder_or_unlimited",
  "out_of_stock",
];

const GROUP_DETAILS: Record<
  AvailabilityGroup,
  { label: string; description: string; color: string }
> = {
  in_stock: {
    label: "Produits avec du stock",
    description: "Mode standard avec une quantité disponible supérieure à zéro.",
    color: "green",
  },
  preorder_or_unlimited: {
    label: "Précommande ou stock illimité",
    description: "Produits vendables sans dépendre du stock disponible.",
    color: "blue",
  },
  out_of_stock: {
    label: "Produits sans stock",
    description: "Mode standard avec une quantité disponible égale à zéro.",
    color: "default",
  },
};

const normalizeOrder = (value: unknown): AvailabilityGroup[] => {
  if (
    Array.isArray(value) &&
    value.length === DEFAULT_ORDER.length &&
    new Set(value).size === DEFAULT_ORDER.length &&
    DEFAULT_ORDER.every((group) => value.includes(group))
  ) {
    return [...(value as AvailabilityGroup[])];
  }
  return [...DEFAULT_ORDER];
};

type Props = {
  warehouseId: number;
  canEdit: boolean;
};

const ProductDisplayOrderSettings: React.FC<Props> = ({
  warehouseId,
  canEdit,
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [stores, setStores] = useState<StoreDisplayOrder[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<number | null>(null);
  const [order, setOrder] = useState<AvailabilityGroup[]>([...DEFAULT_ORDER]);

  const selectedStore = useMemo(
    () => stores.find((store) => store.id === selectedStoreId) || null,
    [selectedStoreId, stores]
  );

  const loadSettings = useCallback(
    async (signal?: AbortSignal) => {
      setLoading(true);
      setError("");
      try {
        const response = await fetch(
          `${API_CONFIG.BASE_URL}/api/ecommerce/stores/catalog-display-order?warehouse_id=${warehouseId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            },
            signal,
          }
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || "Chargement de l'ordre impossible.");
        }

        const nextStores: StoreDisplayOrder[] = Array.isArray(data.stores)
          ? data.stores.map((store: StoreDisplayOrder) => ({
              ...store,
              id: Number(store.id),
              product_stock_order: normalizeOrder(store.product_stock_order),
            }))
          : [];
        setStores(nextStores);
        setSelectedStoreId((current) =>
          nextStores.some((store) => store.id === current)
            ? current
            : nextStores[0]?.id ?? null
        );
      } catch (loadError: any) {
        if (loadError?.name !== "AbortError") {
          setStores([]);
          setSelectedStoreId(null);
          setError(loadError?.message || "Chargement de l'ordre impossible.");
        }
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [warehouseId]
  );

  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    loadSettings(controller.signal);
    return () => controller.abort();
  }, [loadSettings, open]);

  useEffect(() => {
    if (selectedStore) {
      setOrder(normalizeOrder(selectedStore.product_stock_order));
    }
  }, [selectedStore]);

  const moveGroup = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= order.length) return;

    setOrder((current) => {
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const saveOrder = async () => {
    if (!selectedStoreId || !canEdit) return;
    setSaving(true);
    setError("");
    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/ecommerce/stores/${selectedStoreId}/catalog-display-order`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            warehouse_id: warehouseId,
            product_stock_order: order,
          }),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || "Enregistrement de l'ordre impossible.");
      }

      const savedOrder = normalizeOrder(data.product_stock_order);
      setOrder(savedOrder);
      setStores((current) =>
        current.map((store) =>
          store.id === selectedStoreId
            ? { ...store, product_stock_order: savedOrder }
            : store
        )
      );
      message.success("Ordre d'affichage enregistré.");
    } catch (saveError: any) {
      setError(saveError?.message || "Enregistrement de l'ordre impossible.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        icon={<SettingOutlined />}
        onClick={() => setOpen(true)}
        className="ecommerce-admin-hero-button"
      >
        Ordre d&apos;affichage
      </Button>

      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        title="Ordre d'affichage des produits"
        width={640}
        destroyOnClose
        footer={
          <Space>
            <Button onClick={() => setOpen(false)}>Fermer</Button>
            {canEdit && (
              <Button
                type="primary"
                onClick={saveOrder}
                loading={saving}
                disabled={!selectedStoreId || loading}
              >
                Enregistrer l&apos;ordre
              </Button>
            )}
          </Space>
        }
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Alert
            type="info"
            showIcon
            message="Classement initial de la vitrine"
            description="Cet ordre est proposé par défaut. Si un client choisit ensuite un tri par nom ou par prix, son choix prend le relais."
          />

          {error && <Alert type="error" showIcon message={error} />}
          {!canEdit && (
            <Alert
              type="warning"
              showIcon
              message="Consultation uniquement"
              description="La permission de modification des fiches produit est requise pour enregistrer cet ordre."
            />
          )}

          {loading ? (
            <div className="ecommerce-product-order-loading">
              <Spin />
            </div>
          ) : stores.length === 0 ? (
            <Empty description="Aucune boutique accessible n'utilise cet établissement" />
          ) : (
            <>
              {stores.length > 1 ? (
                <div>
                  <Text strong>Boutique concernée</Text>
                  <Select
                    value={selectedStoreId ?? undefined}
                    onChange={(value) => setSelectedStoreId(Number(value))}
                    style={{ width: "100%", marginTop: 8 }}
                    options={stores.map((store) => ({
                      value: store.id,
                      label: store.store_name,
                    }))}
                  />
                </div>
              ) : (
                <Text>
                  Boutique concernée : <strong>{stores[0].store_name}</strong>
                </Text>
              )}

              <div className="ecommerce-product-order-list">
                {order.map((group, index) => {
                  const details = GROUP_DETAILS[group];
                  return (
                    <div className="ecommerce-product-order-item" key={group}>
                      <Tag color={details.color}>{index + 1}</Tag>
                      <div className="ecommerce-product-order-copy">
                        <Text strong>{details.label}</Text>
                        <Text type="secondary">{details.description}</Text>
                      </div>
                      <Space size="small">
                        <Tooltip title="Monter ce groupe">
                          <Button
                            aria-label={`Monter ${details.label}`}
                            icon={<ArrowUpOutlined />}
                            onClick={() => moveGroup(index, -1)}
                            disabled={!canEdit || index === 0}
                          />
                        </Tooltip>
                        <Tooltip title="Descendre ce groupe">
                          <Button
                            aria-label={`Descendre ${details.label}`}
                            icon={<ArrowDownOutlined />}
                            onClick={() => moveGroup(index, 1)}
                            disabled={!canEdit || index === order.length - 1}
                          />
                        </Tooltip>
                      </Space>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </Space>
      </Modal>
    </>
  );
};

export default ProductDisplayOrderSettings;
